import { yupResolver } from '@hookform/resolvers/yup';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import { PasswordElement, TextFieldElement } from 'react-hook-form-mui';
import { useNavigate, useSearchParams } from 'react-router';
import * as Yup from 'yup';
import { Box, Stack, Typography } from '@mui/material';

import { Link, Logo } from '@barboxx/admin-components';
import Api from '@barboxx/api';
import { PasswordToken } from '@barboxx/models';
import { PASSWORD_REGEX, PASSWORD_REQUIREMENTS_MESSAGE } from '@barboxx/types';

import {
    Footer,
    RootContainer,
    StyledForm,
    SubmitButton,
} from '../StyledComponents';

const RESET_PASSWORD_SCHEMA = Yup.object().shape({
    email: Yup.string()
        .email('Please enter a valid email address')
        .required('Please enter your email address'),
    password: Yup.string()
        .matches(PASSWORD_REGEX, PASSWORD_REQUIREMENTS_MESSAGE)
        .required('Please enter a new password'),
});

type FormValues = Omit<PasswordToken.ResetPasswordRequest, 'token'>;

export default function ResetPassword() {
    const navigate = useNavigate();
    const notifications = useNotifications();
    const [searchParams] = useSearchParams();

    const token = searchParams.get('token');

    const formContext = useForm<FormValues>({
        resolver: yupResolver(RESET_PASSWORD_SCHEMA),
    });

    const onSubmit = (values: FormValues) => {
        if (token) {
            return Api.Authentication.Users.resetPassword({ ...values, token })
                .then(response => {
                    if (response.ok) {
                        notifications.show('Password reset', {
                            severity: 'success',
                        });

                        navigate('/login', { replace: true });
                    } else {
                        throw response.data;
                    }
                })
                .catch((error: Error) => {
                    notifications.show(error.message, {
                        severity: 'error',
                    });
                });
        }
        return;
    };

    return (
        <RootContainer maxWidth="xs">
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
            >
                <Logo />
                <Typography
                    margin={2}
                    variant="h6"
                    align="center"
                    color="textPrimary"
                >
                    Business Portal
                </Typography>

                <StyledForm onSuccess={onSubmit}>
                    <Stack gap={2}>
                        <TextFieldElement
                            label="Email"
                            name="email"
                            fullWidth
                            required
                            autoComplete="email"
                            autoFocus
                        />
                        <PasswordElement
                            label="Password"
                            name="password"
                            fullWidth
                            type="password"
                            required
                            autoComplete="current-password"
                        />
                        <SubmitButton
                            type="submit"
                            color="primary"
                            variant="contained"
                            fullWidth
                            loading={formContext.formState.isSubmitting}
                        >
                            Reset Password
                        </SubmitButton>
                    </Stack>
                </StyledForm>

                <Link
                    marginTop={1}
                    alignSelf="flex-end"
                    color="textSecondary"
                    to="/login"
                >
                    Go back to sign in?
                </Link>
                <Footer>
                    <Typography color="textPrimary" variant="body2">
                        <Link to="/signup">Sign up</Link> for barboxx business
                        portal!
                    </Typography>
                </Footer>
            </Box>
        </RootContainer>
    );
}
